// EF-11 — the candidate-side half of backend/files/answer_files.py: the same
// checks the server runs, so the candidate is told BEFORE the bytes travel
// (the server still decides; a file that slips past here is refused there
// with the same words). Pure, no DOM, unit-tested.

// `max_bytes` is optional on the wire (the server defaults it to 10 MB when
// a rules blob omits it), so the helpers default the same way.
export interface FileRulesLike {
  allowed_extensions: string[];
  max_bytes?: number;
}

export const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;

export function maxBytesOf(rules: FileRulesLike): number {
  return rules.max_bytes ?? DEFAULT_MAX_BYTES;
}

export type FileCheck = { ok: true } | { ok: false; message: string };

// A 0-byte file. Explorer's "New > Microsoft Word Document" creates exactly
// that, and it looks like any other document until it is opened and saved —
// so the message says what to do, not only what is wrong (Sheet #13). The same
// words as backend `answer_files.EMPTY_FILE_MESSAGE`.
// ⚠ Placeholder copy — the wording is Emilia's.
export const EMPTY_FILE_MESSAGE =
  "This file is empty (0 KB), so there is nothing to upload. If you just created it, open it, write your answer and save it — then choose it again.";

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** `pdf` → `.pdf`; the list the candidate reads and the 415 message names. */
export function describeAllowed(extensions: string[]): string {
  return extensions.map((ext) => `.${ext}`).join(", ");
}

/** The `<input accept>` value — extensions only, never MIME guesses. */
export function acceptAttribute(extensions: string[]): string {
  return extensions.map((ext) => `.${ext}`).join(",");
}

export function extensionOf(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? "";
  const stripped = base.replace(/^\.+/, "");
  const dot = stripped.lastIndexOf(".");
  if (dot <= 0) return "";
  return stripped.slice(dot + 1).toLowerCase();
}

/** The size limit as the recruiter set it, in whole MB (never "0 MB"). */
export function limitMb(rules: FileRulesLike): number {
  return Math.max(1, Math.floor(maxBytesOf(rules) / (1024 * 1024)));
}

export function checkFileAgainstRules(
  file: { name: string; size: number },
  rules: FileRulesLike,
): FileCheck {
  const ext = extensionOf(file.name);
  if (!ext || !rules.allowed_extensions.includes(ext)) {
    return {
      ok: false,
      message: `Only these file types are accepted here: ${describeAllowed(rules.allowed_extensions)}.`,
    };
  }
  if (file.size === 0) {
    return { ok: false, message: EMPTY_FILE_MESSAGE };
  }
  if (file.size > maxBytesOf(rules)) {
    return {
      ok: false,
      message: `The file is over the ${limitMb(rules)} MB limit for this question.`,
    };
  }
  return { ok: true };
}

// --- The recruiter's download (S3 remainder, pre-ship 2026-09-13) -----------
//
// The download is `attachment` + `application/octet-stream` + nosniff, and
// `backend/files/answer_files.download_filename` appends `.txt` to the three
// extensions a desktop OS RUNS on a double-click. That covers the browser and
// the file manager. What none of it can do is make a candidate's file safe to
// OPEN: a .zip is a container for anything, and a spreadsheet's formulas run in
// Excel when the recruiter enables content. We deliberately do not rewrite the
// bytes — it is the candidate's answer and the evidence, and `escape_formula`
// belongs to the exports WE author, not to a file they uploaded. So the
// recruiter is told before the click, and only where there is something to say.

const DOWNLOAD_CAUTIONS: Record<string, string> = {
  zip: "A .zip can hold anything, including programs. Extract it somewhere isolated and don't run what's inside.",
  csv: "Formulas are stored exactly as the candidate wrote them — nothing is stripped. Open it in Protected View and don't enable content or external links.",
  xlsx: "Formulas are stored exactly as the candidate wrote them — nothing is stripped. Open it in Protected View and don't enable content or external links.",
};

/** What to warn the recruiter about before they download this file, or null. */
export function downloadCaution(filename: string): string | null {
  return DOWNLOAD_CAUTIONS[extensionOf(filename)] ?? null;
}
